import { effectiveSuggestionHtml } from '@seen/core/models/suggestionStagingBasic'
import { getSchema, type JSONContent } from '@tiptap/core'
import { DOMParser as PMDOMParser, type Schema } from '@tiptap/pm/model'
import type { Extensions } from '@tiptap/react'
import type {
  AIProposal,
  ProposalRender,
} from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import { getRenderExtensions } from '#src/components/rich-text-editor/extensions.ts'

/* The suggestion-content extension factory: the node + mark schema every
 * surface that renders or parses PENDING suggestion content shares — the
 * preview root (1.2) and, later, the island editor and accept-time parsing. It
 * is the host editor's render set MINUS the aiProposals behavior extension: a
 * preview root renders one proposal's content and must never recursively stage
 * proposals within itself.
 *
 * It is deliberately NOT filtered by insertPolicy. The render path must display
 * whatever can be legitimately STAGED — a replaceNode op stages a replace-target
 * field node (choice / observation / validated-tool) whose insertPolicy is not
 * 'insertable' — so an insertPolicy filter (empty of custom field nodes at Phase
 * 1.1) would drop every field preview and reintroduce the render drift this path
 * exists to remove. insertPolicy admissibility governs what may be INSERTED or
 * pasted (the Phase 2 island), never what may be rendered.
 *
 * A function, not a const, for the same import-cycle reason getRenderExtensions
 * is (see extensions.ts): evaluating the node bindings lazily at call time
 * avoids the TDZ a top-level array literal would hit. */
export function getSuggestionContentExtensions(): Extensions {
  return getRenderExtensions().filter((ext) => ext.name !== 'aiProposals')
}

/* The HTML for a resolved op's content by render kind. A taskItem op carries an
 * orphan <li> whose <ul data-type="taskList"> wrapper this layer supplies (parity
 * with the former innerHTML widget); every other render kind uses its content as
 * authored. The single source of truth shared by the preview root (1.2) and the
 * accept pipeline (1.3) so the two never drift on the wrap — an accept that
 * re-parsed a taskItem op WITHOUT it would insert an orphan <li>. */
export function suggestionContentHtml(
  render: ProposalRender,
  htmlContent: string
): string {
  return render.type === 'taskItem'
    ? `<ul data-type="taskList">${htmlContent}</ul>`
    : htmlContent
}

/* The HTML a proposal's preview root renders. The preview root parses this
 * through the real schema, so no raw HTML string is ever written to the DOM.
 * Routed through `effectiveSuggestionHtml`, so a suggestion the clinician
 * island-edited previews the edit — the content its accept would insert — not
 * the AI's superseded original. An edit arrives already wrapped (it is the
 * island document's own HTML), which is why the wrap applies only to the op. */
export function suggestionPreviewHtml(proposal: AIProposal): string {
  return effectiveSuggestionHtml(
    proposal,
    suggestionContentHtml(proposal.render, proposal.htmlContent)
  )
}

/* The top-level nodes an accept inserts for a suggestion's HTML, parsed through
 * the SAME doc-level parse the preview root renders (DOMParser.parse with
 * preserveWhitespace), so the accepted block structure is the previewed block
 * structure.
 *
 * Handing raw HTML to Tiptap's insertContentAt instead parses it with
 * DOMParser.parseSlice, whose open top keeps the whitespace-only text runs
 * BETWEEN block tags that a doc-level parse discards — and each surviving run
 * lands as a paragraph of its own, the blank line that appeared in the note but
 * never in the preview. AI-authored HTML carries exactly such runs whenever it
 * indents or newline-separates its blocks.
 *
 * Inline-only content (a replaceNode field, a plain text insert) is returned as
 * the wrapping paragraph's inline children, so it still joins the paragraph at
 * the insertion point rather than opening a block of its own. */
export function suggestionInsertNodes(
  schema: Schema,
  html: string
): JSONContent[] {
  const el = document.createElement('div')
  el.innerHTML = html
  const parser = PMDOMParser.fromSchema(schema)
  const slice = parser.parseSlice(el, { preserveWhitespace: true })
  if (slice.content.size === 0) {
    return []
  }
  let hasBlockContent = false
  slice.content.forEach((node) => {
    hasBlockContent ||= node.isBlock
  })
  const doc = parser.parse(el, { preserveWhitespace: true })
  const nodes =
    !hasBlockContent && doc.firstChild ? doc.firstChild.content : doc.content
  return (nodes.toJSON() as JSONContent[] | null) ?? []
}

/* The island's admission filter (design §2.a, invariant 1). A hard, fail-closed
 * ALLOW-LIST of the extension names the editable island schema admits: plain
 * content + the mention node (for condition mentions). Every side-effectful
 * custom node — choice, observation, validated-tool, document-reference,
 * signature-button, encounter*, the block/insert-button families, columns,
 * template-variable — is absent, so an unconverted node type cannot enter an
 * island through ANY path (AI seed, island edit, or paste): a node the schema
 * lacks is dropped on parse. A node type added later is excluded by default —
 * the invariant is a schema guarantee, not a runtime check, so it must not
 * silently admit the next family before that family's conversion PR.
 *
 * Mention is admitted structurally (one PM type spans every mention subtype),
 * and every subtype passes through the island untouched. insertPolicy governs
 * what the AI may INTRODUCE into a note; it does not govern what may SURVIVE a
 * clinician editing the text around it. Filtering by it here would silently
 * delete citations (QuestionnaireResponse, DocumentReference, …) the clinician
 * never touched, on the seed path as well as on paste. */
const ISLAND_ADMISSIBLE_EXTENSION_NAMES = new Set([
  'starterKit',
  'typography',
  'taskList',
  'taskItem',
  'image',
  'mention',
])

/* The editable island schema (design §2.a). The read-only preview factory
 * (getSuggestionContentExtensions) MINUS every node the admission allow-list
 * excludes — same shared node views, filtered set. The local island editor is
 * built from this so its schema cannot represent a side-effectful node at all.
 * The mention node is reconfigured with conditionsOnly, which scopes the
 * island's @-picker MENU only (scopePickerOptions in MentionNode) and never
 * touches document content: a non-condition picker option would insert a node
 * type this schema lacks, or pull a whole prior note in as raw HTML. Read-only
 * consumers keep using the unfiltered factory unchanged. */
export function getIslandEditorExtensions(): Extensions {
  return getSuggestionContentExtensions()
    .filter((ext) => ISLAND_ADMISSIBLE_EXTENSION_NAMES.has(ext.name))
    .map((ext) =>
      ext.name === 'mention' ? ext.configure({ conditionsOnly: true }) : ext
    )
}

/* The island schema, built once lazily (never at module init — the extension
 * factories dodge an import-cycle TDZ by evaluating their node bindings at call
 * time; a top-level getSchema would defeat that). Used to parse the seed off
 * the editor. */
let cachedIslandSchema: Schema | null = null
function islandSchema(): Schema {
  cachedIslandSchema ??= getSchema(getIslandEditorExtensions())
  return cachedIslandSchema
}

/* Parse the AI-op seed HTML into island content (design §2.a). Admission is the
 * island schema itself: a node type it lacks is dropped by this parse. Mentions
 * of every resource type are carried through verbatim — the seed is the content
 * the clinician is about to reword, and its citations belong to that content,
 * not to the AI's insert policy. Returns doc JSON for the local editor's initial
 * content (already parsed, so it needs no further schema round-trip). */
export function seedIslandContent(html: string): JSONContent {
  const el = document.createElement('div')
  el.innerHTML = html
  return PMDOMParser.fromSchema(islandSchema())
    .parse(el, { preserveWhitespace: true })
    .toJSON()
}
